import type { createFileRoute } from '@tanstack/react-router';
import { redirect } from '@tanstack/react-router';
import { Schema, Predicate } from 'effect';
import { optionalCatching } from '@/lib/search-params';
import { rootLogger } from '@/lib/root-logger';
import { getSession } from '@/services/get-session';
import { markWebSessionEnd } from '@/services/session-end';
import {
  BillingPeriod,
  rememberBillingPeriod,
} from '@/lib/checkout/billing-period';

const moduleLogger = rootLogger.child({ module: 'AuthRoute' });

/**
 * Where to send the user once they are signed in. Set by `_authenticated`'s
 * route guard when it ejects an unauthenticated visitor, so `/app/login`
 * can hand them back to the page they were reaching for.
 *
 * Total by construction — see `optionalCatching`. A junk value drops to
 * absent and the redirect falls through to `/app/home`, which is the same
 * behaviour the previous `z.string().optional().catch(undefined)` had.
 */
const AuthSearchParams = Schema.Struct({
  redirectTo: optionalCatching(Schema.String),
  billingPeriod: optionalCatching(BillingPeriod),
});

type AuthSearchParams = typeof AuthSearchParams.Type;

const authSearchParamsSchema = Schema.toStandardSchemaV1(AuthSearchParams);

export const authRouteOptions = () =>
  ({
    validateSearch: authSearchParamsSchema,
    // `rememberBillingPeriod` writes per-tab state that must not run in the
    // server's shared module scope.
    ssr: false,
    beforeLoad: async ({
      search,
      preload,
    }: {
      search: AuthSearchParams;
      preload: boolean;
    }) => {
      if (!preload && search.billingPeriod)
        rememberBillingPeriod(search.billingPeriod);
      const check = await getSession();
      // Only a positively verified session skips the login page; on
      // `Unavailable` the page renders and the sign-in attempt itself
      // settles the question.
      if (Predicate.isTagged(check, 'Authenticated')) {
        moduleLogger.info('User already logged in, redirecting');
        throw redirect({
          to: search.redirectTo ?? '/app/home',
        });
      }
      // `_authenticated` redirects a signed-out hard load on the server, where
      // session telemetry has no storage, so the browser records the ending
      // here. Already-reported and never-signed-in cases are no-ops.
      if (!preload && Predicate.isTagged(check, 'Unauthenticated'))
        markWebSessionEnd({ cause: 'route_guard_no_session' });
    },
  }) satisfies Parameters<ReturnType<typeof createFileRoute>>[0];
